import {Capacitor} from "@capacitor/core";
import {SocialLogin} from '@capgo/capacitor-social-login';
import ThirdParty from 'supertokens-web-js/recipe/thirdparty'
import {ref} from "vue";
import {useRouter} from "vue-router";
import {SignInWithApple, SignInWithAppleOptions} from '@capacitor-community/apple-sign-in';
import {AdminRouteNames} from "@app/router/route-names";
import {GoogleLoginResponseOnline} from "@capgo/capacitor-social-login/dist/esm/definitions";
import {useToast} from "vue-toastification";
import { trackEvent } from '@app/composables/posthog';
import { useAuthError } from '@app/composables/auth-error';

export const useSignInUp = () => {
  const router = useRouter();
  const toast = useToast();
  const { setAuthError } = useAuthError();

  const googleSignLoading = ref(false);
  const appleSignLoading = ref(false);
  const showLoginWithApple = Capacitor.getPlatform() === 'ios';

  const onGooglePressed = async (queryParams: Record<string, string>) => {
    trackEvent('social_login_clicked', { provider: 'google' });
    if (Capacitor.isNativePlatform()) {
      try {
        googleSignLoading.value = true;

        console.log("Starting Google login flow...");

        const response = await SocialLogin.login({
          provider: 'google',
          options: {
            scopes: ['email', 'profile'],
            forceRefreshToken: true
          }
        });

        console.log("Google login raw response:", JSON.stringify(response));

        if (response.provider !== 'google') {
          throw new Error("Invalid provider in response");
        }

        const googleResponse = response.result as GoogleLoginResponseOnline;

        // Add clientType to specify which Google client config to use
        const body = {
          thirdPartyId: "google",
          clientType: Capacitor.getPlatform() === 'ios' ? 'ios' : 'web', // Specify platform-specific client
          clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          oAuthTokens: {
            id_token: googleResponse.idToken,
            access_token: googleResponse.accessToken?.token
          }
        };

        console.log("Sending request to backend:", JSON.stringify(body));

        // Send the request to your backend
        const params = Object.keys(queryParams).filter(k => queryParams[k])
          .map(k => `${k}=${encodeURIComponent(queryParams[k])}`)
          .join('&');
        const signInUpResponse = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/auth/signinup${params.length > 0 ? '?' + params : ''}`,
          {
            method: 'post',
            body: JSON.stringify(body),
            headers: {
              rid: "thirdpartyemailpassword",
              'content-type': 'application/json',
              originPlatform: Capacitor.getPlatform()
            }
          });

        const backendResponseData = await signInUpResponse.json();
        console.log("Backend response:", JSON.stringify(backendResponseData));

        // Email already registered with email+password — the backend rejected the Google sign-up.
        if (backendResponseData.status === "GENERAL_ERROR") {
          setAuthError(backendResponseData.message || 'לא הצלחנו לחבר אותך. אנא נסו שוב');
          return;
        }

        if (backendResponseData.status !== "OK") {
          throw new Error(`Backend login failed: ${JSON.stringify(backendResponseData)}`);
        }

        console.log("Login successful, redirecting...");
        await router.replace({name: AdminRouteNames.Home});

      } catch (error) {
        console.error("Google login error:", error);
        toast.error('לא הצלחנו לחבר אותך. אנא נסו שוב')
      } finally {
        googleSignLoading.value = false;
      }
    } else if (Capacitor.getPlatform() === 'web') {
      // Web flow remains the same
      const authUrl = await ThirdParty.getAuthorisationURLWithQueryParamsAndSetState({
        thirdPartyId: 'google',
        frontendRedirectURI: `${import.meta.env.VITE_DASHBOARD_URL}/auth/callback/google`,
      });
      window.location.assign(authUrl);
    }
  };

  const onApplePressed = async () => {
    trackEvent('social_login_clicked', { provider: 'apple' });
    if (showLoginWithApple) {
      try {
        appleSignLoading.value = true;

        const options: SignInWithAppleOptions = {
          clientId: import.meta.env.VITE_APPLE_SIGN_IN_CLIENT_ID || '',
          redirectURI: `${import.meta.env.VITE_DASHBOARD_URL}/auth/callback/apple`,
          scopes: 'email name',
          // state: '12345',
          nonce: 'nonce',
        };

        const {response: appleResponse} = await SignInWithApple.authorize(options);
        console.log('appleResponse', JSON.stringify(appleResponse))
        const body = {
          thirdPartyId: "apple",
          clientId: options.clientId,
          redirectURIInfo: {
            redirectURIOnProviderDashboard: options.redirectURI,
            redirectURIQueryParams: {
              code: appleResponse.authorizationCode,
            },
          },
        };
        console.log('body', body)
        const signInUpResponse = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/auth/signinup`,
          {
            method: 'post',
            body: JSON.stringify(body),
            headers: {
              rid: "thirdpartyemailpassword",
              'content-type': 'application/json'
            }
          });

        const response = await signInUpResponse.json();
        if (response.status !== "OK") {
          console.log(response)
          throw new Error("Apple login failed");
        }
        await router.replace({name: AdminRouteNames.Home});
      } catch (e) {
        console.log(`failed with ${e}`)
        toast.error('לא הצלחנו לחבר אותך. אנא נסו שוב')
      } finally {
        appleSignLoading.value = false;
      }
    }
  }

  return {
    showLoginWithApple,
    appleSignLoading,
    googleSignLoading,
    onGooglePressed,
    onApplePressed
  }
}
